import AppBar from '@mui/material/AppBar';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import BackIcon from '@mui/icons-material/ArrowBack';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSnackbar } from '~/containers/Snackbar/SnackbarContext';
import ContentView from '~/components/ContentView';
import AvailabilityForm from '~/components/AvailabilityForm';
import moment from 'moment';
import { RecurrentApplicationRule } from '~/components/AvailabilityList';
import messages from './messages';
import { useCutoffDates } from '~/hooks/api/useCutoffDates';
import { useRecurrentApplication } from '~/hooks/api/useRecurrentApplication';
import { useEditRecurrentApplication } from '~/hooks/api/useEditRecurrentApplication';
import { useSettingsByCategory } from '~/hooks/api/useSettingsByCategory';
import { usePublisherMe } from '~/hooks/api/usePublisherMe';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';

const route = getRouteApi('/availability/$recurrentApplicationId');

const AvailabilityEdit = () => {
  const intl = useIntl();
  const { showSnackbar } = useSnackbar();
  const params = route.useParams();
  const search = route.useSearch();
  const navigate = useNavigate();
  const publisherQuery = usePublisherMe();

  const cutOffDatesQuery = useCutoffDates();
  const vehicleWorkflowSettingQuery = useSettingsByCategory('planning');
  const publisherId = search.publisherId || publisherQuery.data?.id.toString();
  const recurrentApplicationQuery = useRecurrentApplication(
    publisherId ? parseInt(publisherId, 10) : undefined,
    parseInt(params.recurrentApplicationId, 10)
  );
  const mutation = useEditRecurrentApplication(
    publisherId ? parseInt(publisherId, 10) : undefined,
    parseInt(params.recurrentApplicationId, 10),
    {
      onError() {
        showSnackbar({
          message: intl.formatMessage(messages.failureMessage),
        });
      },
      onSuccess() {
        showSnackbar({
          message: intl.formatMessage(messages.successMessage),
        });
        navigate({
          to: search.from || '/availability',
          search: {
            publisherId,
          },
        });
      },
    }
  );

  return (
    <ContentView
      header={
        <AppBar position="static">
          <Toolbar>
            <Grid
              container
              spacing={2}
              sx={{
                alignItems: 'center',
              }}
            >
              <Grid>
                <IconButton
                  color="inherit"
                  href=""
                  component={Link}
                  to={search.from || `/availability?publisherId=${publisherId}`}
                  aria-label={intl.formatMessage(messages.back)}
                  size="large"
                >
                  <BackIcon />
                </IconButton>
              </Grid>
              <Grid>
                <Typography
                  variant="h6"
                  sx={{
                    color: 'inherit',
                  }}
                >
                  <FormattedMessage {...messages.title} />
                </Typography>
              </Grid>
            </Grid>
          </Toolbar>
        </AppBar>
      }
      isLoading={
        publisherQuery.isLoading ||
        recurrentApplicationQuery.isLoading ||
        cutOffDatesQuery.isLoading ||
        vehicleWorkflowSettingQuery.isLoading
      }
    >
      {recurrentApplicationQuery.data ? (
        <AvailabilityForm
          renderDescription={({ startTime, endTime, startingDate }) => (
            <FormattedMessage
              {...messages.takesEffectAfterDate}
              values={{
                startTime,
                endTime,
                startingDate: moment(startingDate).format('L'),
              }}
            />
          )}
          onSubmit={(values) => {
            mutation.mutate({
              end: moment(values.end).format('YYYY-MM-DDTHH:mm:ss'),
              start: moment(values.start).format('YYYY-MM-DDTHH:mm:ss'),
              rule: values.rule,
              hasVehicle: values.hasVehicle || false,
            });
          }}
          initialValues={{
            ...recurrentApplicationQuery.data,
            start: new Date(recurrentApplicationQuery.data.start),
            end: new Date(recurrentApplicationQuery.data.end),
            rule: recurrentApplicationQuery.data
              .rule as RecurrentApplicationRule,
          }}
          shouldShowVehicleAvailabilityField={
            vehicleWorkflowSettingQuery.data?.isVehicleWorkflowActive === true
          }
        />
      ) : null}
    </ContentView>
  );
};

export default AvailabilityEdit;
